import { Box, Menu } from '@zillow/constellation-10';
import {
  IconArchiveOutline,
  IconInboxOutline,
  IconSpamFolderOutline,
  IconStar0Percent,
} from '@zillow/constellation-icons';
import type { Persona } from '../../types/persona';
import type { Brand } from '../../types/brand';
import { useEffect, useState } from 'react';
import FolderSelectorMenuItem from './FolderSelectorMenuItem';

export enum Folder {
  Archived = 'ARCHIVED',
  Favorite = 'FAVORITE',
  Inbox = 'INBOX',
  ShowAll = 'SHOW_ALL',
  Spam = 'SPAM',
}

type MenuItemConfig = {
  label: string;
  icon: React.ReactNode;
  folder: Folder;
  isActive: boolean;
};

export type FolderActions = {
  onFolderChange: (folderOption: Folder) => void;
};

export type FolderSelectorProps = {
  persona?: Persona;
  brand?: Brand;
  selectedFolder: Folder;
  actions: FolderActions;
};

export default function FolderSelector({
  persona = 'landlord',
  brand = 'zillow',
  selectedFolder,
  actions,
}: FolderSelectorProps) {
  const [folder, setFolder] = useState<Folder>(selectedFolder);
  const [isActive, setIsActive] = useState(false);
  const { onFolderChange } = actions;
  const isZillowBrand = brand === 'zillow';
  const isPersonaRenter = persona === 'renter';

  useEffect(() => {
    setFolder(selectedFolder);
  }, [selectedFolder]);

  const label = (() => {
    if (folder === Folder.Archived) {
      return 'Archived';
    } else if (folder === Folder.Spam) {
      return isZillowBrand && isPersonaRenter ? 'Spam' : 'Reported';
    } else if (folder === Folder.Favorite) {
      return 'Starred';
    }
    return isZillowBrand && isPersonaRenter ? 'Recent' : 'Inbox';
  })();

  const getMenuItems = (): Array<MenuItemConfig> => {
    if (isPersonaRenter) {
      return [
        {
          label: isZillowBrand ? 'Recent' : 'Inbox',
          icon: <IconInboxOutline />,
          folder: Folder.Inbox,
          isActive: isViewInbox,
        },
        {
          label: 'Archived',
          icon: <IconArchiveOutline />,
          folder: Folder.Archived,
          isActive: isViewArchive,
        },
        {
          label: isZillowBrand ? 'Spam' : 'Reported',
          icon: <IconSpamFolderOutline />,
          folder: Folder.Spam,
          isActive: isViewSpam,
        },
      ];
    }

    return [
      {
        label: 'Inbox',
        icon: <IconInboxOutline />,
        folder: Folder.Inbox,
        isActive: isViewInbox,
      },
      {
        label: 'Starred',
        icon: <IconStar0Percent />,
        folder: Folder.Favorite,
        isActive: isViewFavorites,
      },
      {
        label: 'Archived',
        icon: <IconArchiveOutline />,
        folder: Folder.Archived,
        isActive: isViewArchive,
      },
      {
        label: 'Reported',
        icon: <IconSpamFolderOutline />,
        folder: Folder.Spam,
        isActive: isViewSpam,
      },
    ];
  };

  const getHandleViewClickFn = (folderOption: Folder) => {
    return () => {
      if (folderOption !== folder) {
        setFolder(folderOption);
        setIsActive(false);
        onFolderChange(folderOption);
      }
    };
  };

  const { isViewArchive, isViewInbox, isViewSpam, isViewFavorites } = (() => {
    return {
      isViewInbox: folder === Folder.Inbox,
      isViewArchive: folder === Folder.Archived,
      isViewSpam: folder === Folder.Spam,
      isViewFavorites: folder === Folder.Favorite,
    };
  })();

  const handleTargetClick = () => {
    setIsActive(!isActive);
  };

  const menuItems = getMenuItems();

  return (
    <Menu
      css={{ width: '355px', mdDown: { width: '200px' } }}
      dividers
      shouldAwaitInteractionResponse
      content={
        <Menu.Group aria-label="Folder Selector Menu">
          <Menu.Heading level={3} css={{ textTransform: 'capitalize' }}>
            View
          </Menu.Heading>
          <Box css={{ padding: 'default', mdDown: { padding: '0px' } }}>
            {menuItems.map(({ label, icon, folder, isActive }) => (
              <FolderSelectorMenuItem
                key={label}
                itemLabel={label}
                icon={icon}
                onItemClick={getHandleViewClickFn(folder)}
                isActive={isActive}
              />
            ))}
          </Box>
        </Menu.Group>
      }
      trigger={
        <Menu.Button onClick={handleTargetClick} data-testid="folder-selector-button">
          {label}
        </Menu.Button>
      }
    />
  );
}
